import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
 
import {
  Typography,
  IconButton,
  Grid,
  Paper,
} from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { ArrowBack } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import MilestoneTrackingAPI from "apis/MilestoneTracking/MilestoneTracking";
import Context from "context/context";
import { toast } from "react-toastify";


/* ── Gauge geometry constants ── */
const CX = 420;
const CY = 380;
const R_OUTER = 310;
const R_INNER = 200;
const GAP_DEG = 1;
 
/* ── Helper: polar → cartesian ── */
function pt(cx, cy, r, deg) {
  const rad = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}
 
/* ── Helper: arc segment path ── */
function segPath(cx, cy, rI, rO, startDeg, endDeg) {
  const [ox1, oy1] = pt(cx, cy, rO, startDeg);
  const [ox2, oy2] = pt(cx, cy, rO, endDeg);
  const [ix1, iy1] = pt(cx, cy, rI, endDeg);
  const [ix2, iy2] = pt(cx, cy, rI, startDeg);
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${ox1} ${oy1}`,
    `A ${rO} ${rO} 0 ${large} 1 ${ox2} ${oy2}`,
    `L ${ix1} ${iy1}`,
    `A ${rI} ${rI} 0 ${large} 0 ${ix2} ${iy2}`,
    "Z",
  ].join(" ");
}
 
function valueToAngle(v, maxValue) {
  return 180 + (Math.min(v, maxValue) / maxValue) * 180;
}

const useStyles = makeStyles(() => ({
  page: {
    minHeight: "100vh",
    background: "#F5F6FA",
    padding: 30,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  wrapper: {
    width: "100%",
    maxWidth: 1200,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: 20,
  },
  backBtn: {
    background: "#d0e8f5",           // was #E8ECF4
    marginRight: 15,
    color: "#0a72ba",                // was #1e3a8a
    "&:hover": { background: "#b8d8ee" }, // was #D8DFF0
  },
  titleBlock: {},
  title: {
    fontSize: 26,
    fontWeight: 800,
    color:  "#1A1C2E",               // was #1A1C2E
    letterSpacing: 1,
  },
  subTitle: {
    fontSize: 13,
    color: "#6B7280",               // was #6B7280
  },
  hero: {
    background: "linear-gradient(135deg, #0a72ba 0%, #40b5e4 100%)", // was #1e3a8a→#2563eb
    borderRadius: 20,
    padding: "22px 28px",
    color: "#fff",
    marginBottom: 20,
  },
  heroTitle: {
    fontWeight: 800,
    fontSize: 20,
  },
  heroSub: {
    marginTop: 4,
    opacity: 1,
    fontSize: 13,
  },
  statsRow: {
    marginTop: 18,
  },
  statCard: {
    background: "rgba(255,255,255,0.18)",
    borderRadius: 14,
    padding: "14px 16px",
    textAlign: "center",
    border: "1px solid rgba(255,255,255,0.25)",
  },
  statLabel: {
    fontSize: 11,
    opacity: 2,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 800,
    marginTop: 6,
  },
  statSub: {
    fontSize: 11,
    opacity: 2,
    marginTop: 3,
  },
  mainCard: {
    background: "#FFFFFF",
    borderRadius: 20,
    padding: "28px 28px 20px",
    color: "#1A1C2E",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: "1px solid #d0e8f5",    // was #E5E9F2
  },
  gaugeHeading: {
    fontSize: 22,
    fontWeight: 800,
    marginBottom: 2,
    color: "#1A1C2E",               // was #1A1C2E
  },
  gaugeSub: {
    fontSize: 13,
    color: "#9CA3AF",               // was #9CA3AF
    marginBottom: 6,
  },
  gaugeOuter: {
    position: "relative",
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
  legend: {
    display: "flex",
    gap: 20,
    marginTop: -195,
    justifyContent: "center",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#0a72ba",               // was #6B7280
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 2,
  },
}));
 
export default function GrowthBonusDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const { shiptomilestoneView, getMilestoneConfig } = MilestoneTrackingAPI();
  const { state, dispatch } = useContext(Context);
  const location = useLocation();
 
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState([]);
  const [maxValue, setMaxValue] = useState(110000);
  const [tooltip, setTooltip] = useState(null);
  const [shipto, setShipto] = useState("");
  const [animationStarted, setAnimationStarted] = useState(true);

  const [needleAngle, setNeedleAngle] = useState(180);
  const animationRef = useRef(null);
  const gaugeCardRef = useRef(null);
  const animationStartedRef = useRef(false);
  const dataRef = useRef(null);
  const maxValueRef = useRef(110000);
 
  const tooltipOffset = 50;
 
  useEffect(() => {
    if (location?.state?.data) {
      const dataObj = location.state.data;
      setShipto(dataObj?.customer_account_number || "");
    } else {
      history.goBack();
    }
  }, [location, history]);
 
  useEffect(() => {
    dispatch({
      type: "SET_PATHNAME",
      payload: ["HOME", "ADMIN", "MILESTONE", "VIEW"],
    });
  }, [dispatch]);
 
  useEffect(() => {
    async function fetchMilestoneConfig() {
      try {
        const res = await getMilestoneConfig();
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          const sorted = [...res.data].sort((a, b) => a.milestone_value - b.milestone_value);
          const values = sorted.map((m) => Number(m.milestone_value));
          setMilestones(values);
          setMaxValue(values[values.length - 1]);
        }
        // } else {
        //   const defaults = [
        //     10000, 15000, 20000, 25000, 30000,
        //     35000, 40000, 45000, 50000, 55000,
        //     60000, 65000, 70000, 75000, 80000,
        //     85000, 90000, 95000, 100000, 105000,
        //     110000,
        //   ];
        //   setMilestones(defaults);
        //   setMaxValue(110000);
        // }
      } catch (err) {
        console.error("Failed to load milestone config:", err);
        const defaults = [
          10000, 15000, 20000, 25000, 30000,
          35000, 40000, 45000, 50000, 55000,
          60000, 65000, 70000, 75000, 80000,
          85000, 90000, 95000, 100000, 105000,
          110000,
        ];
        setMilestones(defaults);
        setMaxValue(110000);
      }
    }
    fetchMilestoneConfig();
  }, []);
 
  useEffect(() => {
    if (shipto) {
      getData();
    }
  }, [shipto]);
 
  async function getData() {
    if (!shipto) {
      setLoading(false);
      return;
    }
    try {
      const values = { customer_account_number: shipto };
      dispatch({ type: "SET_LOADING", payload: true });
      setLoading(true);
      const res = await shiptomilestoneView(values);
      if (res?.data) {
        setData(res.data);
      }
    } catch (e) {
      toast.error("Failed to fetch milestone data");
    } finally {
      setLoading(false);
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }
 
  useEffect(() => {
    if (!gaugeCardRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationStarted) {
            setAnimationStarted(true);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(gaugeCardRef.current);
    return () => observer.disconnect();
  }, [gaugeCardRef, animationStarted]);
 
  useEffect(() => {
    if (!data || !animationStarted || milestones.length === 0) return;
    const currentSpend = Number(data?.netQualifyingSpend);
    const targetAngle = valueToAngle(currentSpend, maxValue);
    const PHASE1_DURATION = 2000;
    const PHASE2_DURATION = 2000;
    const PHASE3_DURATION = 2800;
    let startTime = null;
    let phase = 1;
    function easeInOutCubic(t) {
      return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    function easeOutQuart(t) {
      return 1 - Math.pow(1 - t, 4);
    }
    function animate(timestamp) {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      if (phase === 1) {
        const t = Math.min(elapsed / PHASE1_DURATION, 1);
        setNeedleAngle(180 + easeInOutCubic(t) * 180);
        if (t < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          phase = 2;
          startTime = timestamp;
          animationRef.current = requestAnimationFrame(animate);
        }
      } else if (phase === 2) {
        const t = Math.min(elapsed / PHASE2_DURATION, 1);
        setNeedleAngle(360 - easeInOutCubic(t) * 180);
        if (t < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          phase = 3;
          startTime = timestamp;
          animationRef.current = requestAnimationFrame(animate);
        }
      } else if (phase === 3) {
        const t = Math.min(elapsed / PHASE3_DURATION, 1);
        setNeedleAngle(180 + easeOutQuart(t) * (targetAngle - 180));
        if (t < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setNeedleAngle(targetAngle);
        }
      }
    }
    setNeedleAngle(180);
    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [data, animationStarted, milestones, maxValue]);
 
  const currentSpend = Number(data?.netQualifyingSpend || 0);
  const earnedDollars = Number(data?.bonusDollar || 0);
  const earnedPoints = Number(data?.bonusPoints || 0);
  const milestonesHitFromPoints = data?.milestonesHit || 0;
 
  const TOTAL = milestones.length;
  const segSpan = TOTAL > 0 ? 180 / TOTAL : 0;
  const halfGap = GAP_DEG / 2;
 
  const remainingToMax = Math.max(maxValue - currentSpend, 0);
  const pct = Math.round((Math.min(currentSpend, maxValue) / maxValue) * 100);
  const nextMile = milestones.find((m) => currentSpend < m) || null;
  const toNext = nextMile ? nextMile - currentSpend : 0;
 
  const [nx, ny] = pt(CX, CY, R_INNER - 22, needleAngle);
 
  const heroStats = [
    {
      label: "Current Net Qualifying Spend",
      value: `$${currentSpend.toLocaleString()}`,
      sub: `${pct}% of $${(maxValue / 1000).toFixed(0)}K target`,
    },
    {
      label: "Previously Earned",
      value: `$${earnedDollars.toLocaleString()}`,
      sub: `${milestonesHitFromPoints} milestones (${earnedPoints.toLocaleString()} points)`,
    },
    {
      label: `Remaining to $${(maxValue / 1000).toFixed(0)}K`,
      value: `$${remainingToMax.toLocaleString()}`,
      sub: nextMile
        ? `$${toNext.toLocaleString()} to next milestone`
        : "🎉 Target reached!",
    },
  ];
 
  const SVG_W = 780;
  const SVG_H = 650;
 
  if (loading || milestones.length === 0) {
    return <div>Loading...</div>;
  }
 
  return (
    <div className={classes.page}>
      <div className={classes.wrapper}>
 
        {/* ── HEADER ── */}
        <div className={classes.header}>
          <IconButton className={classes.backBtn} onClick={() => history.goBack()}>
            <ArrowBack />
          </IconButton>
          <div className={classes.titleBlock}>
            <div className={classes.title}>THRIVE UNLOCKED</div>
            <div className={classes.subTitle}>
              Growth Bonus Dashboard - <strong>{shipto}</strong>
            </div>
          </div>
        </div>
 
        {/* ── BLOCK 1: HERO STATS ── */}
        <Paper className={classes.hero} elevation={3}>
          <div className={classes.heroTitle}>Growth Bonus Analytics</div>
          <div className={classes.heroSub}>
            Track milestone progress from $0 to ${(maxValue / 1000).toFixed(0)}K
          </div>
          <Grid container spacing={2} className={classes.statsRow}>
            {heroStats.map((s, i) => (
              <Grid item xs={4} key={i}>
                <div className={classes.statCard}>
                  <div className={classes.statLabel}>{s.label}</div>
                  <div className={classes.statValue}>{s.value}</div>
                  <div className={classes.statSub}>{s.sub}</div>
                </div>
              </Grid>
            ))}
          </Grid>
        </Paper>
 
        {/* ── BLOCK 2: GAUGE CARD ── */}
        <Paper className={classes.mainCard} elevation={4} ref={gaugeCardRef}>
          <div className={classes.gaugeHeading}>Growth Bonus Milestone Progress</div>
          <div className={classes.gaugeSub}>
            Milestone journey · $0 → ${(maxValue / 1000).toFixed(0)}K
          </div>
 
          <div className={classes.gaugeOuter}>
            <svg
              width="100%"
              height={SVG_H}
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              style={{ display: "block", overflow: "visible", maxWidth: SVG_W }}
              onClick={() => setTooltip(null)}
            >
              {/* TRACK background */}
              <path
                d={segPath(CX, CY, R_INNER, R_OUTER, 180, 360)}
                fill="#e8f4fc"          // was #EEF1F8
              />
 
              {milestones.map((m, i) => {
                const startValue = i === 0 ? 0 : milestones[i - 1];
                const endValue = m;
                const sDeg = valueToAngle(startValue, maxValue) + halfGap;
                const eDeg = valueToAngle(endValue, maxValue) - halfGap;
                const midDeg = (sDeg + eDeg) / 2;
                const active = currentSpend >= m;
                const isNext = currentSpend >= startValue && currentSpend < m;
                const labelR = (R_INNER + R_OUTER) / 2;
                const [lx, ly] = pt(CX, CY, labelR, midDeg);
                const labelRotate = midDeg - 270;
 
                return (
                  <g key={i}>
                    <path
                      d={segPath(CX, CY, R_INNER, R_OUTER, sDeg, eDeg)}
                      fill={active ? "#0a72ba" : isNext ? "#40b5e4" : "#93d8f0"}
                      // was: active→#2563eb  isNext→#93C5FD  locked→#D1DCF5
                      stroke="#ffffff"
                      strokeWidth="1.5"
                      style={{ cursor: "pointer", transition: "fill 0.2s" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setTooltip({ value: m, active, isNext });
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.setAttribute(
                          "fill",
                          active ? "#085d99" : "#6ebce2"
                          // was: active→#1d4ed8  hover→#60A5FA
                        );
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.setAttribute(
                          "fill",
                          active ? "#0a72ba" : isNext ? "#40b5e4" : "#93d8f0"
                        );
                      }}
                    />
 
                    <g transform={`translate(${lx},${ly}) rotate(${labelRotate})`}>
                      <text
                        textAnchor="middle"
                        dy="-3"
                        fontSize="10"
                        fontFamily="DM Sans, sans-serif"
                        fontWeight="600"
                        fill={active ? "#ffffff" : isNext ? "#ffffff" : "#0a72ba"}
                        // was: active→#fff  isNext→#1e3a8a  locked→#6B84B8
                      >
                        {m >= 1000 ? `$${m / 1000}k` : `$${m}`}
                      </text>
                      <text
                        textAnchor="middle"
                        dy="10"
                        fontSize="9"
                        fontFamily="DM Mono, monospace"
                        fill={active ? "#93d8f0" : isNext ? "#d0f0ff" : "#40b5e4"}
                        // was: active→#BFDBFE  isNext→#1e40af  locked→#8AAAD8
                      >
                        M{i + 1}
                      </text>
                    </g>
                  </g>
                );
              })}
 
              {/* END LABELS */}
              {[
                { label: "$0", deg: 180 },
                { label: `$${maxValue / 1000}k`, deg: 360 },
              ].map(({ label, deg }) => {
                const [lx, ly] = pt(CX, CY, R_OUTER + 26, deg);
                return (
                  <text
                    key={label}
                    x={lx}
                    y={ly}
                    textAnchor="middle"
                    fontSize="13"
                    fontFamily="DM Mono, monospace"
                    fill="#40b5e4"       // was #9CA3AF
                  >
                    {label}
                  </text>
                );
              })}
 
              {/* PROGRESS ARC */}
              {(() => {
                const targetAngle = valueToAngle(currentSpend, maxValue);
                const sDeg = 180 + halfGap;
                const eDeg = targetAngle - halfGap;
                if (eDeg <= sDeg) return null;
                return (
                  <path
                    d={segPath(CX, CY, R_INNER - 12, R_INNER - 2, sDeg, eDeg)}
                    fill="#6ebce2"      // was #00aaff
                    opacity="0.55"      // was 0.22 — slightly more visible
                  />
                );
              })()}
 
              {/* NEEDLE shadow */}
              <line
                x1={CX}
                y1={CY + 3}
                x2={pt(CX, CY + 3, R_INNER - 22, needleAngle)[0]}
                y2={pt(CX, CY + 3, R_INNER - 22, needleAngle)[1]}
                stroke="rgba(10,114,186,0.18)"  // was rgba(0,0,0,0.12)
                strokeWidth="8"
                strokeLinecap="round"
              />
              {/* NEEDLE main */}
              <line
                x1={CX}
                y1={CY}
                x2={nx}
                y2={ny}
                stroke="#0a72ba"        // was #1e3a8a
                strokeWidth="6"
                strokeLinecap="round"
              />
              {/* Hub */}
              <circle cx={CX} cy={CY} r="18" fill="#0a72ba" />  {/* was #2563eb */}
              <circle cx={CX} cy={CY} r="10" fill="#40b5e4" />  {/* was r=8 #fff — added mid ring */}
              <circle cx={CX} cy={CY} r="4"  fill="#ffffff" />  {/* was r=3 #2563eb */}

              {/* TOOLTIP (click on segment) */}
              {tooltip ? (
                <g>
                  <rect
                    x={CX - 75}
                    y={CY - 60 - tooltipOffset}
                    width={150}
                    height={90}
                    rx={12}
                    fill="#fff"
                    stroke="#E5E9F2"    // was #E5E9F2
                    strokeWidth="1.5"
                    filter="url(#cardShadow)"
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor: "default" }}
                  />
                  <text
                    x={CX}
                    y={CY - 38 - tooltipOffset}
                    textAnchor="middle"
                    fontSize="10"
                    fontFamily="DM Sans, sans-serif"
                    fontWeight="700"
                    fill="#0a72ba"      // was #2563eb
                  >
                    Milestone {milestones.indexOf(tooltip.value) + 1}
                  </text>
                  <text
                    x={CX}
                    y={CY - 14 - tooltipOffset}
                    textAnchor="middle"
                    fontSize="18"
                    fontFamily="DM Sans, sans-serif"
                    fontWeight="800"
                    fill="#1A1C2E"      // was #1A1C2E
                  >
                    ${tooltip.value.toLocaleString()}
                  </text>
                  <text
                    x={CX}
                    y={CY + 6 - tooltipOffset}
                    textAnchor="middle"
                    fontSize="11"
                    fontFamily="DM Sans, sans-serif"
                    fontWeight="600"
                    fill={
                      tooltip.active
                        ? "#16a34a"
                        : tooltip.isNext
                        ? "#f59e0b"     // was #f59e0b
                        : "#94a3b8"     // was #94a3b8
                    }
                  >
                    {tooltip.active
                      ? "✓ Unlocked"
                      : tooltip.isNext
                      ? "→ Next target"
                      : "Locked"}
                  </text>
                  {!tooltip.active && (
                    <text
                      x={CX}
                      y={CY + 22 - tooltipOffset}
                      textAnchor="middle"
                      fontSize="10"
                      fontFamily="DM Sans, sans-serif"
                      fill="#9CA3AF"    // was #9CA3AF
                    >
                      ${(tooltip.value - currentSpend).toLocaleString()} more to unlock
                    </text>
                  )}
                </g>
              ) : (
                // Default centered info box
                <g>
                  <rect
                    x={CX - 80}
                    y={CY - 60 - tooltipOffset}
                    width={160}
                    height={85}
                    rx={12}
                    fill="#fff"
                    stroke="#E5E9F2"    // was #E5E9F2
                    strokeWidth="1.5"
                    filter="url(#cardShadow)"
                  />
                  <text
                    x={CX}
                    y={CY - 38 - tooltipOffset}
                    textAnchor="middle"
                    fontSize="10"
                    fontFamily="DM Sans, sans-serif"
                    fontWeight="700"
                    fill="#0a72ba"      // was #2563eb
                  >
                    CURRENT SPEND
                  </text>
                  <text
                    x={CX}
                    y={CY - 12 - tooltipOffset}
                    textAnchor="middle"
                    fontSize="20"
                    fontFamily="DM Sans, sans-serif"
                    fontWeight="800"
                    fill="#1A1C2E"      // was #1A1C2E
                  >
                    ${currentSpend.toLocaleString()}
                  </text>
                  <text
                    x={CX}
                    y={CY + 10 - tooltipOffset}
                    textAnchor="middle"
                    fontSize="10"
                    fontFamily="DM Sans, sans-serif"
                    fill="#9CA3AF"      // was #9CA3AF
                  >
                    {pct}% of ${(maxValue / 1000).toFixed(0)}K target
                  </text>
                </g>
              )}
 
              {/* Drop shadow filter */}
              <defs>
                <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow
                    dx="0"
                    dy="4"
                    stdDeviation="8"
                    floodColor="#0a72ba"   // was #2563eb
                    floodOpacity="0.10"
                  />
                </filter>
              </defs>
            </svg>
          </div>
 
          {/* LEGEND */}
          <div className={classes.legend}>
            {[
              { color: "#0a72ba", label: "Achieved" },    // was #2563eb
              { color: "#40b5e4", label: "Next target" }, // was #93C5FD
              { color: "#93d8f0", label: "Locked" },      // was #D1DCF5
            ].map(({ color, label }) => (
              <div className={classes.legendItem} key={label}>
                <div
                  className={classes.legendDot}
                  style={{ background: color }}
                />
                {label}
              </div>
            ))}
          </div>

          {/* Notes */}
          <div
            style={{
              marginTop: 20,
              padding: "16px 20px",
              borderRadius: 14,
              background: "linear-gradient(135deg, #e8f4fc 0%, #d0e8f5 100%)", // was eff6ff→dbeafe
              border: "1px solid #6ebce2",   // was #bfdbfe
              color: "#0a72ba",              // was #1e3a8a
              boxShadow: "0 2px 8px rgba(10,114,186,0.08)", // was rgba(37,99,235,0.08)
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                marginBottom: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              ℹ️ Important Note
            </div>
            <div style={{ color: "#1e4a6e" }}>  {/* was #334155 */}
              Credits and returns reduce your qualifying spend balance.
              Previously earned Growth Bonuses are not reversed,
              but credits may increase the amount needed to reach your next milestone.
            </div>
          </div>
        </Paper>
      </div>
    </div>
  );
}
